import React, { useEffect, useState } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones';
import { QRCodeCanvas } from 'qrcode.react'; // IMPORTANTE PARA EL QR
import { 
    StartCapsGame, 
    ShuffleCapsGame, 
    SelectCapAdmin, 
    ResetCapsGame, 
    EmergencyKickCapsPlayers, 
    ConnectWebSocketForCapsAdmin, 
    DisconnectWebSocketForCapsAdmin, 
    ShowCapsFrontPageBL 
} from '../../../utilities/BL/Caps';

const Caps = ({ getOutOfTheDynamic }) => {
    // ==========================================================================
    // ESTADOS DE CONFIGURACIÓN
    // ==========================================================================
    const [shuffleDuration, setShuffleDuration] = useState(5);
    const [winMode, setWinMode] = useState(0); 
    const [prizeText, setPrizeText] = useState("");
    const [shuffleSpeedMs, setShuffleSpeedMs] = useState(300); 

    // ==========================================================================
    // ESTADOS DE LA PARTIDA Y RED
    // ==========================================================================
    const [matchPhase, setMatchPhase] = useState('WAITING');
    const [winningCapId, setWinningCapId] = useState(null); 
    const [selectedCapId, setSelectedCapId] = useState(null); 
    const [revealData, setRevealData] = useState(null);
    const [isWorking, setIsWorking] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [wsStatus, setWsStatus] = useState('idle');

    // NUEVO: ESTADO PARA EL MODAL DEL QR
    const [showQRModal, setShowQRModal] = useState(false);
    const playerUrl = `${window.location.origin}/caps`;

    // ==========================================================================
    // WEBSOCKET
    // ==========================================================================
    useEffect(() => {
        ConnectWebSocketForCapsAdmin(handleIncomingMessage, setWsStatus);
        return () => DisconnectWebSocketForCapsAdmin();
    }, []);

    const handleIncomingMessage = (msg) => {
        const payload = msg.payload || {};
        if (msg.type === 'caps.progress') {
            setMatchPhase(payload.phase);
            setWinningCapId(payload.winningCapId);
            setSelectedCapId(payload.selectedCapId);
            setRevealData(payload.revealData);
        } else if (msg.type === 'caps.reset') {
            setMatchPhase('WAITING');
            setWinningCapId(null);
            setSelectedCapId(null);
            setRevealData(null);
        }
    };

    // ==========================================================================
    // ACCIONES
    // ==========================================================================
    const handleShowFrontPage = async () => {
        setIsWorking(true); setErrorMessage('');
        await ShowCapsFrontPageBL();
        setIsWorking(false);
    };

    const handleStartSequence = async () => {
        setIsWorking(true); 
        setErrorMessage('');
        
        const res = await StartCapsGame({ 
            shuffleDurationSeconds: shuffleDuration, 
            winMode, 
            prizeText,
            shuffleSpeedMs 
        });

        if (!res.success) {
            setErrorMessage(res.message);
            setIsWorking(false);
            return;
        }

        setTimeout(async () => {
            await ShuffleCapsGame();
            setIsWorking(false);
        }, 3000);
    };

    const handleSelectCap = async (capId) => {
        setIsWorking(true); setErrorMessage('');
        await SelectCapAdmin(capId);
        setIsWorking(false);
    };

    const handleReset = async () => {
        setIsWorking(true); setErrorMessage('');
        await ResetCapsGame();
        setIsWorking(false);
    };

    const handleEmergency = async () => {
        if (!window.confirm("🚨 ¿Expulsar al jugador actual?")) return;
        setIsWorking(true); setErrorMessage('');
        await EmergencyKickCapsPlayers();
        await ResetCapsGame();
        setIsWorking(false);
    };

    const handleReturnToDashboard = async () => {
        await BLResetCurrentDynamic();
        getOutOfTheDynamic();
    };

    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <section className='AdmRunToGoal__Settings pb-5'>
                <p className='AdmRunToGoal__Title'>🧢 Encuentra el balón</p>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    {/* ESTATUS DE RED */}
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3">
                            <div>
                                <p className="mb-1 text-muted small">Estado de Red</p>
                                <span className={`badge ${wsStatus === 'connected' ? 'bg-success' : 'bg-warning'}`}>
                                    {wsStatus === 'connected' ? 'Conectado al Servidor' : wsStatus}
                                </span>
                            </div>
                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase fw-bold" style={{ letterSpacing: '1px' }}>
                                    {matchPhase === 'WAITING' && 'En Espera / Configuración'}
                                    {matchPhase === 'SHUFFLING' && '⏳ Mezclando...'}
                                    {matchPhase === 'SELECTING' && '🎯 Esperando Selección'}
                                    {matchPhase === 'REVEAL_MODAL' && '🏆 Premio Revelado'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* FASE 1: CONFIGURACIÓN */}
                    {matchPhase === 'WAITING' && (
                        <div className="mb-4 bg-light p-3 rounded border">
                            <h6 className="fw-bold mb-3 border-bottom pb-2">1. Configuración de la dinámica</h6>
                            
                            <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Modo de victoria:</label>
                            <select className='form-select mb-3' value={winMode} onChange={e => setWinMode(Number(e.target.value))} disabled={isWorking}>
                                <option value={0}>Al azar (Suerte real)</option>
                                <option value={1}>Gana siempre</option>
                                <option value={2}>Pierde siempre</option>
                            </select>

                            <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Velocidad de movimiento visual:</label>
                            <select className='form-select mb-3' value={shuffleSpeedMs} onChange={e => setShuffleSpeedMs(Number(e.target.value))} disabled={isWorking}>
                                <option value={600}>Lento (Fácil de seguir)</option>
                                <option value={300}>Medio (Normal)</option>
                                <option value={150}>Rápido (Difícil/Confuso)</option>
                            </select>

                            <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Tiempo total revolviendo (Segundos):</label>
                            <input className='form-control' type='number' min={3} max={15} value={shuffleDuration} onChange={e => setShuffleDuration(Number(e.target.value))} disabled={isWorking}/>
                            <small className='text-muted d-block mb-3'>5 segs. = normal. Más de 5 segs. = marea más.</small>

                            <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Premio (opcional):</label>
                            <input className='form-control mb-3' type='text' value={prizeText} onChange={e => setPrizeText(e.target.value)} disabled={isWorking}/>

                            <div className="d-flex gap-2 mt-3">
                                {/* BOTÓN DE PANTALLA */}
                                <button className={`btn btn-info fw-bold shadow-sm w-100 ${isWorking ? 'disabled' : ''}`} onClick={handleShowFrontPage} disabled={isWorking}>
                                    <i className="bi bi-display mb-1 fs-5"></i> Mostrar portada
                                </button>
                            </div>

                            <button className={`btn btn-success fw-bold w-100 mt-3 shadow-sm ${isWorking ? 'disabled' : ''}`} onClick={handleStartSequence} disabled={isWorking}>
                                {isWorking ? (
                                    <><i className="spinner-border spinner-border-sm me-2"></i> Mostrando bolita al público...</>
                                ) : (
                                    <><i className="bi bi-play-fill"></i> Iniciar juego</>
                                )}
                            </button>
                        </div>
                    )}

                    {/* FASE 3: SELECCIÓN Y RAYOS X */}
                    {matchPhase === 'SELECTING' && (
                        <div className="mb-4 bg-light p-3 rounded border text-center">
                            <h5 className="fw-bold text-primary mb-3"><i className="bi bi-eye-fill"></i> Visión de Rayos X</h5>
                            <p className="small text-muted mb-3">La bolita está en la posición iluminada de verde.</p>
                            
                            <div className="d-flex justify-content-center gap-3">
                                {[
                                    { id: 1, label: 'Izquierda' },
                                    { id: 2, label: 'Centro' },
                                    { id: 3, label: 'Derecha' }
                                ].map(pos => (
                                    <button 
                                        key={pos.id}
                                        onClick={() => handleSelectCap(pos.id)}
                                        disabled={isWorking}
                                        className={`btn btn-lg fw-bold shadow-sm p-3 ${winningCapId === pos.id ? 'btn-success border-success border-3' : 'btn-outline-secondary'}`}
                                        style={{ flex: 1 }}
                                    >
                                        {winningCapId === pos.id ? (
                                            <><i className="bi bi-star-fill text-warning d-block mb-1"></i> Posición {pos.id}<br/><small>{pos.label}</small></>
                                        ) : (
                                            <>Posición {pos.id}<br/><small>{pos.label}</small></>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* FASE 4: REVELACIÓN Y RESUMEN */}
                    {matchPhase === 'REVEAL_MODAL' && revealData && (
                        <div className="mb-4 bg-light p-4 rounded border text-center">
                            <h5 className="fw-bold mb-3 border-bottom pb-2">Resultado de la Jugada</h5>
                            
                            <div className="d-flex justify-content-center align-items-center gap-5 mb-4 mt-3">
                                <div>
                                    <p className="text-muted mb-1 fw-bold">El aficionado eligió:</p>
                                    <span className="badge bg-primary fs-3">Posición {selectedCapId}</span>
                                </div>
                                <div>
                                    <p className="text-muted mb-1 fw-bold">La bolita estaba en:</p>
                                    <span className="badge bg-success fs-3">Posición {winningCapId}</span>
                                </div>
                            </div>

                            <div className={`alert ${revealData.isWinner ? 'alert-success' : 'alert-danger'} fs-4 fw-bold shadow-sm`}>
                                {revealData.isWinner ? <i className="bi bi-trophy-fill me-2"></i> : <i className="bi bi-x-circle-fill me-2"></i>}
                                {revealData.text}
                            </div>
                            <button className="btn btn-warning fw-bold w-100 py-3 mt-2 shadow-sm" onClick={handleReset} disabled={isWorking}>
                                <i className="bi bi-arrow-counterclockwise"></i> Limpiar y Volver a Configurar
                            </button>
                        </div>
                    )}

                    <div className="mt-5 border-top pt-5">
                        <p className='text-center'>Instrucciones para abrir el control del jugador para que elija la gorra</p>
                        {/* NUEVO BOTÓN DE CÓDIGO QR */}
                        <button className="btn btn-outline-primary fw-bold shadow-sm w-100" onClick={() => setShowQRModal(true)} disabled={isWorking}>
                            <i className="bi bi-qr-code-scan mb-1 fs-5"></i> Instrucciones de conexión
                        </button>
                    </div>

                    {/* BOTÓN DE EMERGENCIA */}
                    <div className="mt-5 border-top pt-4">
                        <p className='text-center'>¿Deseas desconectar a todos para liberar el control del juego?</p>
                        <button className={`btn btn-outline-danger fw-bold w-100 ${isWorking ? 'disabled' : ''}`} onClick={handleEmergency} disabled={isWorking}>
                            <i className="bi bi-x-octagon-fill me-2"></i> Desconectar a todos
                        </button>
                    </div>

                    {errorMessage && <p className='text-danger fw-bold mt-3 text-center'>{errorMessage}</p>}
                </div>
            </section>

            {/* =======================================================
                MODAL DEL CÓDIGO QR PARA CONECTAR JUGADOR
            ======================================================= */}
            {showQRModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <div className="modal-header bg-dark text-white border-0" style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                                <h5 className="modal-title fw-bold"><i className="bi bi-phone-vibrate me-2"></i> Conectar jugador</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowQRModal(false)}></button>
                            </div>
                            <div className="modal-body text-center p-4">
                                <p className="text-muted mb-4">Escanea este código con la cámara de tu celular para que el usuario elija la gorra.</p>

                                <div className="bg-white p-3 d-inline-block rounded shadow-sm mb-4">
                                    <QRCodeCanvas value={playerUrl} size={200} level={"H"} />
                                </div>

                                <div className="input-group mb-3">
                                    <input type="text" className="form-control text-center text-muted bg-light" value={playerUrl} readOnly />
                                    <button className="btn btn-outline-secondary" onClick={() => { navigator.clipboard.writeText(playerUrl); alert('Enlace copiado al portapapeles'); }}>
                                        <i className="bi bi-copy"></i>
                                    </button>
                                </div>

                                <a href={playerUrl} target="_blank" rel="noreferrer" className="btn btn-primary fw-bold w-100 py-3 rounded shadow-sm mt-2">
                                    <i className="bi bi-box-arrow-up-right me-2"></i> Abrir control en este dispositivo
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SecondaryBase>
    );
};

export { Caps };









// import React, { useEffect, useState } from 'react';
// import { SecondaryBase } from './../Commons/SecondaryBase';
// import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones';
// import { 
//     StartCapsGame, 
//     ShuffleCapsGame, 
//     SelectCapAdmin, 
//     ResetCapsGame, 
//     EmergencyKickCapsPlayers, 
//     ConnectWebSocketForCapsAdmin, 
//     DisconnectWebSocketForCapsAdmin, 
//     ShowCapsFrontPageBL 
// } from '../../../utilities/BL/Caps';


// const Caps = ({ getOutOfTheDynamic }) => {
//     // ==========================================================================
//     // ESTADOS DE CONFIGURACIÓN
//     // ==========================================================================
//     const [shuffleDuration, setShuffleDuration] = useState(5);
//     const [winMode, setWinMode] = useState(0); 
//     const [prizeText, setPrizeText] = useState("");
//     const [shuffleSpeedMs, setShuffleSpeedMs] = useState(300); // 600=Lento, 300=Medio, 150=Rápido

//     // ==========================================================================
//     // ESTADOS DE LA PARTIDA Y RED
//     // ==========================================================================
//     const [matchPhase, setMatchPhase] = useState('WAITING');
//     const [winningCapId, setWinningCapId] = useState(null); 
//     const [selectedCapId, setSelectedCapId] = useState(null); // <-- Faltaba este estado
//     const [revealData, setRevealData] = useState(null);
//     const [isWorking, setIsWorking] = useState(false);
//     const [errorMessage, setErrorMessage] = useState('');
//     const [wsStatus, setWsStatus] = useState('idle');

//     // ==========================================================================
//     // WEBSOCKET
//     // ==========================================================================
//     useEffect(() => {
//         ConnectWebSocketForCapsAdmin(handleIncomingMessage, setWsStatus);
//         return () => DisconnectWebSocketForCapsAdmin();
//     }, []);

//     const handleIncomingMessage = (msg) => {
//         const payload = msg.payload || {};
//         if (msg.type === 'caps.progress') {
//             setMatchPhase(payload.phase);
//             setWinningCapId(payload.winningCapId);
//             setSelectedCapId(payload.selectedCapId); // Recibimos la elección
//             setRevealData(payload.revealData);
//         } else if (msg.type === 'caps.reset') {
//             setMatchPhase('WAITING');
//             setWinningCapId(null);
//             setSelectedCapId(null);
//             setRevealData(null);
//         }
//     };

//     // ==========================================================================
//     // ACCIONES
//     // ==========================================================================
//     const handleShowFrontPage = async () => {
//         setIsWorking(true); setErrorMessage('');
//         await ShowCapsFrontPageBL();
//         setIsWorking(false);
//     };

//     const handleStartSequence = async () => {
//         setIsWorking(true); 
//         setErrorMessage('');
        
//         // Enviamos la nueva variable "shuffleSpeedMs" al backend
//         const res = await StartCapsGame({ 
//             shuffleDurationSeconds: shuffleDuration, 
//             winMode, 
//             prizeText,
//             shuffleSpeedMs 
//         });

//         if (!res.success) {
//             setErrorMessage(res.message);
//             setIsWorking(false);
//             return;
//         }

//         setTimeout(async () => {
//             await ShuffleCapsGame();
//             setIsWorking(false);
//         }, 3000);
//     };

//     const handleSelectCap = async (capId) => {
//         setIsWorking(true); setErrorMessage('');
//         await SelectCapAdmin(capId);
//         setIsWorking(false);
//     };

//     const handleReset = async () => {
//         setIsWorking(true); setErrorMessage('');
//         await ResetCapsGame();
//         setIsWorking(false);
//     };

//     const handleEmergency = async () => {
//         if (!window.confirm("🚨 ¿Expulsar al jugador actual?")) return;
//         setIsWorking(true); setErrorMessage('');
//         await EmergencyKickCapsPlayers();
//         await ResetCapsGame();
//         setIsWorking(false);
//     };

//     const handleReturnToDashboard = async () => {
//         await BLResetCurrentDynamic();
//         getOutOfTheDynamic();
//     };

//     return (
//         <SecondaryBase actionToExecute={handleReturnToDashboard}>
//             <section className='AdmRunToGoal__Settings pb-5'>
//                 <p className='AdmRunToGoal__Title'>🧢 ¿Dónde quedó la bolita?</p>

//                 <div className='AdmRunToGoal__Settings__Controls'>
                    
//                     {/* ESTATUS DE RED */}
//                     <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
//                         <div className="d-flex justify-content-between align-items-center px-3">
//                             <div>
//                                 <p className="mb-1 text-muted small">Estado de Red</p>
//                                 <span className={`badge ${wsStatus === 'connected' ? 'bg-success' : 'bg-warning'}`}>
//                                     {wsStatus === 'connected' ? 'Conectado al Servidor' : wsStatus}
//                                 </span>
//                             </div>
//                             <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
//                                 <p className="mb-0 small text-uppercase fw-bold" style={{ letterSpacing: '1px' }}>
//                                     {matchPhase === 'WAITING' && 'En Espera / Configuración'}
//                                     {matchPhase === 'SHUFFLING' && '⏳ Mezclando...'}
//                                     {matchPhase === 'SELECTING' && '🎯 Esperando Selección'}
//                                     {matchPhase === 'REVEAL_MODAL' && '🏆 Premio Revelado'}
//                                 </p>
//                             </div>
//                         </div>
//                     </div>

//                     {/* FASE 1: CONFIGURACIÓN */}
//                     {matchPhase === 'WAITING' && (
//                         <div className="mb-4 bg-light p-3 rounded border">
//                             <h6 className="fw-bold mb-3 border-bottom pb-2">1. Configuración de la dinámica</h6>
                            
//                             <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Modo de victoria:</label>
//                             <select className='form-select mb-3' value={winMode} onChange={e => setWinMode(Number(e.target.value))} disabled={isWorking}>
//                                 <option value={0}>Al azar (Suerte real)</option>
//                                 <option value={1}>Gana siempre</option>
//                                 <option value={2}>Pierde siempre</option>
//                             </select>

//                             {/* NUEVO: SELECTOR DE VELOCIDAD */}
//                             <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Velocidad de mezclado:</label>
//                             <select className='form-select mb-3' value={shuffleSpeedMs} onChange={e => setShuffleSpeedMs(Number(e.target.value))} disabled={isWorking}>
//                                 <option value={600}>Lento (Fácil de seguir)</option>
//                                 <option value={300}>Medio (Normal)</option>
//                                 <option value={150}>Rápido (Difícil/Confuso)</option>
//                             </select>

//                             <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Tiempo total revolviendo (Segundos):</label>
//                             <input className='form-control' type='number' min={3} max={15} value={shuffleDuration} onChange={e => setShuffleDuration(Number(e.target.value))} disabled={isWorking}/>
//                             <small className='text-muted d-block mb-3'>5 segs. = normal. Más de 5 segs. = marea más.</small>

//                             <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Premio (opcional):</label>
//                             <input className='form-control mb-3' type='text' value={prizeText} onChange={e => setPrizeText(e.target.value)} disabled={isWorking}/>

//                             <button className={`btn btn-info fw-bold w-100 mt-2 shadow-sm ${isWorking ? 'disabled' : ''}`} onClick={handleShowFrontPage} disabled={isWorking}>
//                                 <i className="bi bi-display"></i> Mostrar portada
//                             </button>

//                             <button className={`btn btn-success fw-bold w-100 mt-3 py-3 shadow-sm ${isWorking ? 'disabled' : ''}`} onClick={handleStartSequence} disabled={isWorking}>
//                                 {isWorking ? (
//                                     <><i className="spinner-border spinner-border-sm me-2"></i> Mostrando bolita al público...</>
//                                 ) : (
//                                     <><i className="bi bi-play-fill"></i> Iniciar juego</>
//                                 )}
//                             </button>
//                         </div>
//                     )}

//                     {/* FASE 3: SELECCIÓN Y RAYOS X */}
//                     {matchPhase === 'SELECTING' && (
//                         <div className="mb-4 bg-light p-3 rounded border text-center">
//                             <h5 className="fw-bold text-primary mb-3"><i className="bi bi-eye-fill"></i> Visión de Rayos X</h5>
//                             <p className="small text-muted mb-3">La bolita está en la posición iluminada de verde.</p>
                            
//                             <div className="d-flex justify-content-center gap-3">
//                                 {[
//                                     { id: 1, label: 'Izquierda' },
//                                     { id: 2, label: 'Centro' },
//                                     { id: 3, label: 'Derecha' }
//                                 ].map(pos => (
//                                     <button 
//                                         key={pos.id}
//                                         onClick={() => handleSelectCap(pos.id)}
//                                         disabled={isWorking}
//                                         className={`btn btn-lg fw-bold shadow-sm p-3 ${winningCapId === pos.id ? 'btn-success border-success border-3' : 'btn-outline-secondary'}`}
//                                         style={{ flex: 1 }}
//                                     >
//                                         {winningCapId === pos.id ? (
//                                             <><i className="bi bi-star-fill text-warning d-block mb-1"></i> Posición {pos.id}<br/><small>{pos.label}</small></>
//                                         ) : (
//                                             <>Posición {pos.id}<br/><small>{pos.label}</small></>
//                                         )}
//                                     </button>
//                                 ))}
//                             </div>
//                         </div>
//                     )}

//                     {/* FASE 4: REVELACIÓN Y RESUMEN */}
//                     {matchPhase === 'REVEAL_MODAL' && revealData && (
//                         <div className="mb-4 bg-light p-4 rounded border text-center">
//                             <h5 className="fw-bold mb-3 border-bottom pb-2">Resultado de la Jugada</h5>
                            
//                             <div className="d-flex justify-content-center align-items-center gap-5 mb-4 mt-3">
//                                 <div>
//                                     <p className="text-muted mb-1 fw-bold">El aficionado eligió:</p>
//                                     <span className="badge bg-primary fs-3">Posición {selectedCapId}</span>
//                                 </div>
//                                 <div>
//                                     <p className="text-muted mb-1 fw-bold">La bolita estaba en:</p>
//                                     <span className="badge bg-success fs-3">Posición {winningCapId}</span>
//                                 </div>
//                             </div>

//                             <div className={`alert ${revealData.isWinner ? 'alert-success' : 'alert-danger'} fs-4 fw-bold shadow-sm`}>
//                                 {revealData.isWinner ? <i className="bi bi-trophy-fill me-2"></i> : <i className="bi bi-x-circle-fill me-2"></i>}
//                                 {revealData.text}
//                             </div>
//                             <button className="btn btn-warning fw-bold w-100 py-3 mt-2 shadow-sm" onClick={handleReset} disabled={isWorking}>
//                                 <i className="bi bi-arrow-counterclockwise"></i> Limpiar y Volver a Configurar
//                             </button>
//                         </div>
//                     )}

//                     {/* BOTÓN DE EMERGENCIA */}
//                     <div className="mt-5 border-top pt-4">
//                         <p className='text-center'>¿Deseas desconectar a todos para liberar el control del juego?</p>
//                         <button className={`btn btn-outline-danger fw-bold w-100 ${isWorking ? 'disabled' : ''}`} onClick={handleEmergency} disabled={isWorking}>
//                             <i className="bi bi-x-octagon-fill me-2"></i> Desconectar a todos
//                         </button>
//                     </div>

//                     {errorMessage && <p className='text-danger fw-bold mt-3 text-center'>{errorMessage}</p>}
//                 </div>
//             </section>
//         </SecondaryBase>
//     );
// };

// export { Caps };